import React, { useEffect } from 'react';
import { FormInputCommonProps } from './types';
import Tooltip from 'rc-tooltip';
import { useRanger } from './ranger/react-ranger';
import { Ranger } from './ranger/ranger';
import { classNames } from '../../utils';
import { twMerge } from 'tailwind-merge';

interface SliderInputProps extends FormInputCommonProps {
  min?: number;
  max?: number;
  step?: number;
  steps?: number[];
  ticks?: number[];
  defaultValue?: ReadonlyArray<number>;
  multi?: boolean;
  leftText?: React.ReactNode;
  rightText?: React.ReactNode;
  onChange?: (val: number) => void;
}

export const SliderInput: React.FC<SliderInputProps> = ({
  name,
  min = 0,
  max = 100,
  step = 1,
  steps = [],
  ticks = [],
  defaultValue = [],
  multi,
  leftText,
  rightText,
  onChange,
  disabled,
}) => {
  const [values, setValues] =
    React.useState<ReadonlyArray<number>>(defaultValue);
  const rangerRef = React.useRef<HTMLDivElement>(null);

  const rangerInstance = useRanger<HTMLDivElement>({
    getRangerElement: () => rangerRef.current,
    debug: true,
    values,
    min,
    max,
    stepSize: step,
    steps,
    ticks,
    onDrag: (instance: Ranger<HTMLDivElement>) => {
      setValues(instance.sortedValues);
    },
    onChange: (instance: Ranger<HTMLDivElement>) => {
      onChange?.((multi ? values : values[0]) as number);
    },
  });

  useEffect(() => {
    if (values !== defaultValue) {
      setValues(defaultValue);
    }
  }, [defaultValue]);

  return (
    <div
      className={twMerge(
        'border-hardwood-300 relative w-full rounded-[4px] border-[1px] bg-white px-[12px]',
        disabled ? 'bg-sidewalk-30 pointer-events-none cursor-default' : ''
      )}
    >
      <div className="flex h-[44px] items-center">
        {leftText}
        <div
          ref={rangerRef}
          style={{
            position: 'relative',
          }}
          className="bg-hardwood-300 h-[2px] flex-[1] cursor-pointer rounded-[2px]"
          onMouseDown={(e) => {
            rangerInstance.handlePress(e, 0);
          }}
        >
          <div className="__slider-center-dot  border-hardwood-30 bg-hardwood-400 absolute left-[39%] top-[-4px] h-[10px] w-[10px] translate-x-[-39] rounded-full border-[3px]"></div>
          {rangerInstance
            .getTicks()
            .map(({ value, key, percentage }, index) => (
              <div
                key={key}
                onClick={() => {
                  setValues((prev) => {
                    return [value];
                  });
                }}
                style={{
                  position: 'absolute',
                  top: '5px',
                  left: `${percentage}%`,
                  transform: 'translateX(-39%)',
                }}
              ></div>
            ))}

          {rangerInstance
            .handles()
            .map(
              (
                {
                  value,
                  onKeyDownHandler,
                  onMouseDownHandler,
                  onTouchStart,
                  isActive,
                },
                i
              ) => (
                <Tooltip
                  key={value}
                  placement="top"
                  trigger={['focus', 'hover']}
                  overlay={<span>{value}</span>}
                >
                  <div
                    key={i}
                    onKeyDown={onKeyDownHandler}
                    onMouseDown={(e) => {
                      onMouseDownHandler(e);
                    }}
                    onTouchStart={onTouchStart}
                    role="slider"
                    aria-valuemin={rangerInstance.options.min}
                    aria-valuemax={rangerInstance.options.max}
                    aria-valuenow={value}
                    className={classNames(
                      'absolute top-[50%] cursor-pointer',
                      isActive ? 'z-[1]' : 'z-[0]',
                      'border-width-[2px] border-morton-600 bg-morton-100 h-[14px] w-[14px] rounded-full border-[2px] outline-none'
                    )}
                    style={{
                      left: `${rangerInstance.getPercentageForValue(value)}%`,
                      transform: 'translate(-50%, -50%)',
                    }}
                  >
                    {/* <div className="relative">
                <div
                  className={classNames(
                    "__slider-tip absolute top-[-20px] bg-sidewalk-900 text-[#FFF]",
                    true ? "visible" : "hidden"
                  )}
                >
                  {value}
                </div>
              </div> */}
                  </div>
                </Tooltip>
              )
            )}
        </div>
        {rightText}
      </div>
    </div>
  );
};
